// @flow
import * as React from 'react'
import KeyboardSelectable from 'mz-ui-kit/core/KeyboardSelectable'
import { eventToKey } from 'mz-ui-kit/utils'
import {
  Container,
  Picker,
  ArrowUp,
  Divider,
  Value,
  ArrowDown,
} from './elements'

type Props = {
  value: string,
  onChange?: Function,
  onClickUp: Function,
  onClickDown: Function,
  pattern?: string,
  overwriteMode?: boolean,
  valueProp?: string,
  innerRef?: Function,
}
type State = {
  value: string,
  selectionStart: number,
  selectionEnd: number,
}
type OverwriteParams = {
  prevValue: string,
  newValue: string,
  selectionStart: number,
  selectionEnd: number,
}

class Selector extends React.Component<Props, State> {
  static defaultProps = {
    overwriteMode: true,
  }

  inputRef = null

  selectionRestored = false

  state = {
    value: this.props.value,
    selectionStart: 0,
    selectionEnd: this.props.value.length,
  }

  UNSAFE_componentWillReceiveProps({ value }: Props) {
    this.setState({ value })
  }

  componentDidUpdate() {
    const { selectionStart, selectionEnd } = this.state

    if (!this.selectionRestored) {
      this.inputRef &&
        this.inputRef.setSelectionRange(selectionStart, selectionEnd)
      this.selectionRestored = true
    }
  }

  rememberCursorPosition = () => {
    const { selectionStart, selectionEnd } = this.inputRef || {}

    this.selectionRestored = false

    this.setState({
      selectionStart,
      selectionEnd,
    })
  }

  setInputSelectionRange = () => {
    const { value } = this.state

    this.inputRef && this.inputRef.setSelectionRange(0, value.length)
  }

  handleInputChange = (event: Object) => {
    const { value: newValue } = event.target
    const { value: prevValue, selectionStart, selectionEnd } = this.state
    const { overwriteMode, pattern } = this.props

    const finalValue = overwriteMode
      ? overwriteValue({ prevValue, newValue, selectionStart, selectionEnd })
      : newValue

    this.rememberCursorPosition()

    if (pattern) {
      const isCorrect = new RegExp(pattern).test(finalValue)

      const updatedValue = isCorrect ? finalValue : prevValue

      this.setState({ value: updatedValue })
    } else {
      this.setState({ value: finalValue })
    }
  }

  handleInputKeyDown = (event: Event) => {
    const { onClickUp, onClickDown } = this.props

    this.rememberCursorPosition()

    const key = eventToKey(event)

    if (key === 'ArrowUp') {
      onClickUp && onClickUp()
      event.preventDefault()
    } else if (key === 'ArrowDown') {
      onClickDown && onClickDown()
      event.preventDefault()
    }
  }

  handleInputBlur = () => {
    const { valueProp, onChange } = this.props
    const { value: newValue } = this.state

    if (!onChange) return

    const updatedValue = valueProp ? { [valueProp]: newValue } : newValue

    onChange(updatedValue)
  }

  setInputRef = (c: any) => {
    const { innerRef } = this.props
    this.inputRef = c
    innerRef && innerRef(c)
  }

  render() {
    const { onClickUp, onClickDown } = this.props
    const { value } = this.state

    return (
      <KeyboardSelectable
        render={({ keyDownHandler }) => (
          <Container>
            <Picker
              tabIndex="-1"
              $operation="up"
              onKeyDown={keyDownHandler(onClickUp)}
              onClick={onClickUp}
            >
              <ArrowUp />
            </Picker>
            <Divider />
            <Value
              ref={this.setInputRef}
              value={value}
              onChange={this.handleInputChange}
              onBlur={this.handleInputBlur}
              onKeyDown={this.handleInputKeyDown}
              onClick={this.setInputSelectionRange}
            />
            <Divider />
            <Picker
              tabIndex="-1"
              $operation="down"
              onKeyDown={keyDownHandler(onClickDown)}
              onClick={onClickDown}
            >
              <ArrowDown />
            </Picker>
          </Container>
        )}
      />
    )
  }
}

// Utils
const overwriteValue = ({
  prevValue,
  newValue,
  selectionStart,
  selectionEnd,
}: OverwriteParams) => {
  const hasSelection = selectionEnd > selectionStart

  // when some value selected - just a regular insert using copy-paste
  // when deleted also just return new value
  if (hasSelection || newValue.length < prevValue.length) return newValue

  const startPart = newValue.slice(0, selectionStart)
  const endPart = (prevValue || newValue).slice(selectionStart)

  const diffLength = newValue.length - prevValue.length
  const endPartIndex = prevValue ? selectionStart + diffLength : newValue.length

  const diff = newValue.slice(selectionStart, endPartIndex)

  const finalValue = startPart + diff + endPart.slice(diff.length)

  return finalValue
}

export default Selector
